import React, { useState, useEffect, useRef, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import * as projectClient from '../../models/projects/projectClient';
import Loader from '../common/loader';
import MobileSelector from './mobileSelector';
import ProjectsSelector from './projectsSelector';
import Card from './card';
import PrCard from './prCard';
import type { Project } from '../../models/projects/project';
import type { ProjectsPr } from '../../models/projects/pr';
import type { NewsPicksBannerProps } from '../common/newsPicksBanner';
import { handleHttpError } from '../../../common/http/errorHandler';
import { simpleFlashMessage } from '../../../common/utils/flashMessage';
import { useSearchInput } from '../../hooks/useSearchInput';

interface Props {
  keyword: string | null;
  order: string | null;
  speficInvestorFlg: boolean;
  newsPicksBanner: NewsPicksBannerProps | null;
}

// PRカードの差し込み位置（先頭から数えたカードのindex）。Figmaでは2段目の右=4枚目
const PR_CARD_INDEX = 3;

export default function IndexView({
  keyword: initialKeyword,
  order: initialOrder,
  speficInvestorFlg,
  newsPicksBanner,
}: Props) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [order, setOrder] = useState<string | null>(initialOrder);
  // 取得のトリガーはこの object の identity。同じ並び順をもう一度クリックしたときのように
  // 値が変わらない再取得でも、新しい object を渡せば effect が走る（companies/indexView.tsx と同じ形）
  const [query, setQuery] = useState({
    keyword: initialKeyword ?? '',
    page: 1,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [pr, setPr] = useState<ProjectsPr | null>(null);
  const abortController = useRef<AbortController | null>(null);

  // 取得は確定した検索語でだけ走らせる。1 文字ごとの入力や IME の変換中には取得しない
  const search = useSearchInput({
    initialValue: initialKeyword ?? '',
    onCommit: keyword => {
      setProjects([]);
      setIsLoading(true);
      setHasNextPage(true);
      setQuery({ keyword, page: 1 });
    },
  });

  const fetchProjects = useCallback(() => {
    // 直前の未完了リクエストを中断し、結果の競合（race condition）を防ぐ
    abortController.current?.abort();
    const controller = new AbortController();
    abortController.current = controller;
    projectClient
      .fetchAll(
        query.page,
        query.keyword === '' ? null : query.keyword,
        order,
        controller.signal,
      )
      .then(({ items, hasNext }) => {
        // 1ページ目（新規検索・並び替え）は置換、2ページ目以降（追加読み込み）は追記
        setProjects(prev => (query.page === 1 ? items : [...prev, ...items]));
        setHasNextPage(hasNext);
        setIsLoading(false);
      })
      .catch((error: unknown) => {
        // 中断されたリクエストは後続のリクエストに引き継ぐため何もしない
        if (controller.signal.aborted) return;
        handleHttpError(error, simpleFlashMessage);
        // エラー時はローディングを解除しつつ追加読み込みも止め、
        // InfiniteScroll による再取得ループを防ぐ
        setIsLoading(false);
        setHasNextPage(false);
      });
  }, [order, query]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    // アンマウント時に未完了リクエストを中断し、解放後の state 更新を防ぐ
    return () => abortController.current?.abort();
  }, []);

  useEffect(() => {
    // PRカードの表示判定とリンク先を取得する。取得に失敗しても一覧表示には影響させない（カードを出さないだけ）
    const controller = new AbortController();
    projectClient
      .fetchPr(controller.signal)
      .then(({ pr }) => setPr(pr))
      .catch(() => {
        // アンマウントによる中断時は解放後の state 更新を防ぐため何もしない
        if (controller.signal.aborted) return;
        setPr(null);
      });
    // アンマウント時に未完了リクエストを中断する
    return () => controller.abort();
  }, []);

  const onLoadMore = () => {
    if (!isLoading && hasNextPage) {
      setQuery(current => ({ ...current, page: current.page + 1 }));
      setIsLoading(true);
    }
  };

  const onSubmitSearch = () => {
    // 検索アイコンと Enter は並び順を「全て」に戻す。並び順を選ぶとキーワードが消えるのと対になる挙動。
    // IME の変換中は submit が確定しないので、並び順だけ動かさないよう戻り値で分ける
    if (search.submit()) setOrder(null);
  };

  const onClickOrder = (orderValue: string | null) => {
    if (!isLoading) {
      setProjects([]);
      setIsLoading(true);
      setHasNextPage(true);
      // 並び順を選んだらキーワードを消す。控えていた自動検索もここで取り消される。
      // reset と setQuery には同じ値を渡すこと。ずれると入力欄と query の keyword が食い違い、
      // hooks 側の「同じ語なら再取得しない」判定が次の自動検索で誤って発火する
      search.reset('');
      setQuery({ keyword: '', page: 1 });
      setOrder(orderValue);
    }
  };

  const cards = projects.map(project => (
    <Card
      key={project.id}
      project={project}
      speficInvestorFlg={speficInvestorFlg}
    />
  ));
  if (pr && cards.length > 0) {
    cards.splice(
      Math.min(PR_CARD_INDEX, cards.length),
      0,
      <PrCard key="pr-card" pr={pr} />,
    );
  }

  return (
    <div>
      <div className="row">
        <div className="col d-block d-lg-none mb-10 justify-content-center">
          <MobileSelector
            order={order}
            searchInputProps={search.inputProps}
            onClickOrder={onClickOrder}
            onSubmitSearch={onSubmitSearch}
          />
        </div>
        <div className="col-lg-3 d-none d-lg-block">
          <ProjectsSelector
            order={order}
            newsPicksBanner={newsPicksBanner}
            searchInputProps={search.inputProps}
            onClickOrder={onClickOrder}
            onSubmitSearch={onSubmitSearch}
          />
        </div>
        <div className="col-lg-9 main-row__wrapper">
          <InfiniteScroll
            pageStart={0}
            loadMore={onLoadMore}
            hasMore={hasNextPage}
            loader={<Loader key={0} type="water" />}
            className="row"
          >
            {cards}
          </InfiniteScroll>
          {!isLoading && projects.length === 0 && (
            <div>プロジェクトは見つかりませんでした</div>
          )}
        </div>
        <div className="col d-block d-lg-none">
          <div className="project-selector__attention">
            <span>※</span>
            <span>
              募集ページについては、募集期間終了後、募集の成立不成立に関わらず事業者の要請に応じて非開示となることがあります。ご了承下さい。
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
